<script setup>
/* eslint semi: 1 */
import { h, inject, resolveComponent, getCurrentInstance, computed } from 'vue';
import { createColumnHelper, FlexRender } from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import { FwbButton } from 'flowbite-vue';

import MediaScheduleMixin from '@/components/mixins/media-schedule-mixin';
import { useScheduleList } from '@/composables/schedule/use-schedule-list';
import { useScheduleQuery } from '@/composables/schedule/use-schedule-query';
import SchedulePlaytimePopup from './schedule-playtime-popup.vue';

import playerIconWhite from '@/static/icons/player_icon_white.svg';

defineOptions({
  mixins: [MediaScheduleMixin]
});

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  showLoader: {
    type: Boolean,
    default: false,
  },
  enableDelete: {
    type: Boolean,
    default: false,
  },
  playerStatusById: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  'card-click',
  'delete-click',
  'preview-load',
  'iframe-mouseenter',
  'iframe-mouseleave',
  'setup-click',
]);

const columnHelper = createColumnHelper();
const { t } = useI18n();
const $utils = inject('$utils');
const baseIconSvg = resolveComponent('BaseIconSvg');
const { proxy } = getCurrentInstance();
const { getResolutionText, getStatus, getPreviewAspectRatio, getPlayerUiState } = useScheduleList();
const { isFiltering } = useScheduleQuery();

function getCardKey (item, index) {
  return item?._edit?.uid || item?.scheduleId || item?.id || index;
}

function getPreviewType (item) {
  return item?.preview?.type || null;
}

function isScheduleOnline (item) {
  const rawStatus = props.playerStatusById[item?.scheduleId];
  return rawStatus == null || !['unconfigured', 'failed', 'disconnected'].includes(rawStatus);
}

const columns = computed(() => {
  // Only specific clients have media ads support so we only show the ads duration column if at least one of the items has ads duration
  const hasAdsDuration = props.items.some((item) => !!item?.mediaDurations?.adds);

  return [
    columnHelper.display({
      id: 'preview',
      enableSorting: false,
      size: 170,
      minSize: 170,
      maxSize: 170,
      header: () => '',
      cell: (info) => {
        const item = info?.row?.original;
        if (!item) return h('span', {}, '');

        const previewType = getPreviewType(item);
        const previewChildren = [];

        if (previewType === 'img') {
          const aspect = getPreviewAspectRatio(item);
          previewChildren.push(h('img', {
            src: item.preview?.url,
            alt: item.name,
            class: ['h-full object-cover', aspect.isVertical ? 'block w-auto mx-auto' : 'w-full'],
            style: aspect.isVertical ? { aspectRatio: aspect.ratio } : {},
            onLoad: () => emit('preview-load', item),
          }));
        } else if (['svg', 'html'].includes(previewType)) {
          const aspect = getPreviewAspectRatio(item);
          previewChildren.push(h('iframe', {
            id: `scheduleTablePreview-${item.scheduleId || info.row.id}`,
            src: $utils.filterMediaPreviewUrl(item.preview?.url, { queryParams: { dimensions: item.dimensions } }),
            'data-src': $utils.trimDataSrc(item.preview?.url, { queryParams: { dimensions: item.dimensions } }),
            class: [
              'template-target-on-refresh-token border-0 block object-cover object-center',
              aspect.isVertical ? 'h-full w-auto mx-auto' : 'w-full h-full',
              `media-preview-${item?._edit?.uid || ''}`,
            ],
            style: aspect.isVertical ? { aspectRatio: aspect.ratio } : {},
            onLoad: () => emit('preview-load', item),
            onMouseenter: () => emit('iframe-mouseenter', item),
            onMouseleave: () => emit('iframe-mouseleave', item),
          }));
        } else {
          previewChildren.push(h('div', {
            class: 'flex h-full w-full items-center justify-center bg-gray-100 text-center',
          }, [
            h('span', {
              class: 'px-3 text-xs font-semibold uppercase tracking-wide text-gray-500',
            }, t('label.previewUnavailable')),
          ]));
        }

        return h('div', {
          class: 'relative h-[72px] w-[128px] overflow-hidden rounded-md border border-gray-200 bg-gray-50',
          'data-component-element-id': `schedule-table-item-preview-${getCardKey(item, info.row.index)}`,
        }, previewChildren);
      },
    }),
    columnHelper.display({
      id: 'status',
      enableSorting: false,
      size: 150,
      minSize: 150,
      header: () => t('label.status'),
      cell: (info) => {
        const item = info?.row?.original;
        if (!item) return h('span', {}, '');

        const status = getStatus(isScheduleOnline(item));

        return h('span', {
          class: ['inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-medium', status.class],
          'data-component-element-id': `schedule-table-item-status-${getCardKey(item, info.row.index)}`,
        }, [
          h(baseIconSvg, {
            name: status.icon,
            class: 'w-3.5! h-3.5!',
          }),
          t(status.text),
        ]);
      },
    }),
    columnHelper.display({
      id: 'playerName',
      enableSorting: false,
      size: 240,
      minSize: 240,
      header: () => t('label.playerName'),
      cell: (info) => {
        const item = info?.row?.original;
        if (!item) return h('span', {}, '');

        return h('span', {
          class: 'truncate text-xs font-semibold uppercase tracking-wide text-gray-800',
          'data-component-element-id': `schedule-table-item-name-${getCardKey(item, info.row.index)}`,
        }, item.name || '-');
      },
    }),
    columnHelper.display({
      id: 'playerModel',
      enableSorting: false,
      size: 180,
      minSize: 180,
      header: () => `${t('label.playerModel')}`,
      cell: (info) => {
        const item = info?.row?.original;
        if (!item) return h('span', {}, '');

        return h('span', {
          class: 'text-xs text-gray-600',
          'data-component-element-id': `schedule-table-item-model-${getCardKey(item, info.row.index)}`,
        }, item.deviceType || '-');
      },
    }),
    columnHelper.display({
      id: 'resolution',
      enableSorting: false,
      size: 170,
      minSize: 170,
      header: () => t('label.resolution'),
      cell: (info) => {
        const item = info?.row?.original;
        if (!item) return h('span', {}, '');

        return h('span', {
          class: 'text-xs text-gray-600',
          'data-component-element-id': `schedule-table-item-resolution-${getCardKey(item, info.row.index)}`,
        }, getResolutionText(item.resolution));
      },
    }),
    columnHelper.display({
      id: 'schedule',
      enableSorting: false,
      size: 340,
      minSize: 340,
      header: () => t('label.schedule'),
      cell: (info) => {
        const item = info?.row?.original;
        if (!item) return h('span', {}, '');

        const times = Array.isArray(item.times) ? proxy.filterScheduleTimes(item.times) : [];
        if (times.length < 1) {
          return h('span', { class: 'text-xs text-gray-400' }, '-');
        }

        return h('div', { class: 'flex flex-wrap gap-1 items-center' }, [
          ...times.slice(0, 2).map((time, index) => {
            const isPriority = !!time?.priority;
            const classes = isPriority
              ? 'bg-orange-100 text-orange-800'
              : 'bg-primary-100 text-primary-800';
            const iconName = isPriority ? 'flowbite:star-solid' : 'flowbite:calendar-month-outline';

            return h('div', {
              key: `${getCardKey(item, info.row.index)}-time-${index}`,
              class: ['inline-flex items-center rounded-lg px-2 py-0.5 text-xs font-medium', classes],
              'data-component-element-id': `schedule-table-item-time-${getCardKey(item, info.row.index)}-${index}`,
            }, [
              h(baseIconSvg, {
                name: iconName,
                class: 'mr-1 inline-block w-3.5! h-3.5!',
              }),
              time?._edit?.textLabel || '-',
            ]);
          }),
          times.length > 1 ? h(SchedulePlaytimePopup, { playTimes: times }) : null,
        ]);
      },
    }),
    columnHelper.display({
      id: 'duration',
      enableSorting: false,
      size: 160,
      minSize: 160,
      header: () => t('label.duration'),
      cell: (info) => {
        const item = info?.row?.original;
        if (!item) return h('span', {}, '');

        if (!item.mediaDurations?.content) {
          return h('span', {}, '-');
        }

        return h('span', {
          class: 'inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-500',
          'data-component-element-id': `schedule-table-item-duration-${getCardKey(item, info.row.index)}`,
        }, $utils.formatDurationToMinutesAndSeconds({ duration: item.mediaDurations.content }));
      },
    }),
    columnHelper.display({
      id: 'adsDuration',
      enableSorting: false,
      size: 170,
      minSize: 170,
      header: () => t('label.adsDuration'),
      cell: (info) => {
        const item = info?.row?.original;
        if (!item) return h('span', {}, '');

        if (!item.mediaDurations?.adds) {
          return h('span', { }, '-');
        }

        return h('span', {
          class: 'inline-flex items-center px-2 py-0.5 text-[#121212] text-xs font-medium',
          'data-component-element-id': `schedule-table-item-ads-duration-${getCardKey(item, info.row.index)}`,
        }, $utils.formatDurationToMinutesAndSeconds({ duration: item.mediaDurations.adds }));
      },
    }),
    columnHelper.display({
      id: 'actions',
      enableSorting: false,
      size: 72,
      minSize: 72,
      maxSize: 72,
      header: () => '',
      cell: (info) => {
        const item = info?.row?.original;
        if (!item || !props.enableDelete) return h('span', {}, '');

        if (getPlayerUiState(item) === 'unconfigured') {
          return h(FwbButton, {
            color: 'red',
            class: 'flex items-center justify-center rounded-lg border-0 outline-none',
            onClick: (event) => {
              event.stopPropagation();
              emit('setup-click', item);
            },
          }, {
            prefix: () => h('img', {
              class: 'w-3 h-3 max-w-3',
              src: playerIconWhite,
            }),
            default: () => t('label.setup'),
          });
        }
        return h('button', {
          type: 'button',
          'aria-label': t('label.delete'),
          class: 'inline-flex h-8 w-8 items-center justify-center rounded-full opacity-0 transition-opacity border-0 bg-transparent group-hover:opacity-100',
          'data-component-element-id': `schedule-table-item-delete-btn-${getCardKey(item, info.row.index)}`,
          onClick: (event) => {
            event.stopPropagation();
            emit('delete-click', item);
          },
        }, h('div', {
          class: 'w-full h-full schedule-delete-icon',
        }));
      },
    }),
  ].filter((column) => column.id !== 'adsDuration' || hasAdsDuration);
});
</script>

<template>
  <BaseDataTable
    class="schedule-table-list-wrapper"
    :data="items"
    :columns="columns"
    :show-init-table-loader="showLoader"
    :empty-placeholder="$t('texts.noContentHasBeenScheduledYet')"
    :empty-filtered-placeholder="$t('texts.emptyFilterTablePlaceholder')"
    :is-filtering-state="isFiltering"
    global-filter-type="manual"
    sort-type="client-side"
    pagination-type="load-more"
    disable-height-auto-calculation
  >
    <template #body-row="{ row, index }">
      <tr
        class="schedule-item-row group border-b border-gray-200 bg-white hover:bg-gray-50"
        :data-component-element-id="`schedule-table-row-${index}`"
        @click="$emit('card-click', row.original)"
      >
        <td
          v-for="cell in row.getVisibleCells()"
          :key="cell.id"
          class="h-[64px] px-4 text-xs align-middle"
        >
          <FlexRender
            :render="cell.column.columnDef.cell"
            :props="cell.getContext()"
          />
        </td>
      </tr>
    </template>
  </BaseDataTable>
</template>

<style lang="scss" scoped>
:deep(.schedule-delete-icon) {
  background: url('../../../static/icons/remove.svg') no-repeat center center;
  background-size: contain;
}
</style>
